import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    Crown, Sparkles, Zap, Users, TrendingUp, ArrowRight, Clock,
    MessageSquare, FileText, BrainCircuit, Mail, Mic, Search,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

/* ==========================================================================
   Sprint J-3-D · UsageWidget (AppHome section)
   ==========================================================================
   • Fetches /api/access/summary
   • Owner sees single "Platform Owner · Unlimited" panel
   • Everyone else sees per-feature progress bars + trial banner when active
   • Refreshes on 'wfos:access-changed' event (dispatched after trial start,
     Stripe checkout success, etc.)
   ========================================================================== */

const FEATURE_META = {
    workforce_hire:       { label: "Workforce Members",         Icon: Users,         short: "seats" },
    atlas_message:        { label: "Atlas messages",            Icon: Sparkles,      short: "sent" },
    skill_run:            { label: "Skill runs",                Icon: Zap,           short: "runs" },
    orchestration_run:    { label: "Orchestration runs",        Icon: BrainCircuit,  short: "runs" },
    workspace_ai_write:   { label: "Doc AI writing",            Icon: FileText,      short: "calls" },
    workspace_ai_reply:   { label: "Email AI replies",          Icon: Mail,          short: "sent" },
    workspace_ai_summary: { label: "Meeting AI summaries",      Icon: Mic,           short: "runs" },
    workspace_ai_qa:      { label: "Knowledge AI Q&A",          Icon: Search,        short: "asks" },
};

const PRIMARY_ORDER = ["workforce_hire", "atlas_message", "orchestration_run", "skill_run"];
const SECONDARY_ORDER = ["workspace_ai_write", "workspace_ai_reply", "workspace_ai_summary", "workspace_ai_qa"];

export default function UsageWidget() {
    const { user } = useAuth();
    const [summary, setSummary] = useState(null);
    const [showAll, setShowAll] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/access/summary");
            setSummary(data);
        } catch {
            /* silent; widget just doesn't render */
        }
    }, []);

    useEffect(() => {
        load();
        const onChange = () => load();
        window.addEventListener("wfos:access-changed", onChange);
        return () => window.removeEventListener("wfos:access-changed", onChange);
    }, [load]);

    if (!summary) return null;

    if (summary.owner) {
        return (
            <section
                className="atlas-card p-6 relative overflow-hidden"
                style={{
                    borderColor: "rgba(245,158,11,0.55)",
                    background: "linear-gradient(135deg, rgba(245,158,11,0.10) 0%, rgba(139,92,246,0.10) 100%)",
                }}
                data-testid="usage-widget-owner"
            >
                <div className="flex items-start gap-4">
                    <div
                        className="w-12 h-12 rounded-xl grid place-items-center flex-shrink-0"
                        style={{
                            background: "rgba(245,158,11,0.20)",
                            border: "1px solid rgba(245,158,11,0.55)",
                        }}
                    >
                        <Crown className="w-5 h-5" strokeWidth={1.75} style={{ color: "#fbbf24" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="atlas-label" style={{ color: "#fbbf24" }}>Platform Owner</div>
                        <h3 className="mt-1 text-xl font-semibold" style={{ color: "var(--atlas-text-primary)" }}>
                            Unlimited across all metrics
                        </h3>
                        <p className="mt-1 text-[13px]" style={{ color: "var(--atlas-text-tertiary)" }}>
                            Your workspace bypasses every paywall, seat cap, and monthly quota by design.
                        </p>
                    </div>
                </div>
            </section>
        );
    }

    const usage = summary.usage || {};
    const tier = summary.tier || "free";
    const trial = summary.trial || {};
    const isTrial = trial.in_trial;

    return (
        <section className="atlas-card p-6" data-testid="usage-widget">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                    <div className="atlas-label inline-flex items-center gap-1.5">
                        <TrendingUp className="w-3 h-3" strokeWidth={2} /> Usage this month
                    </div>
                    <h3 className="mt-1 text-lg font-semibold" style={{ color: "var(--atlas-text-primary)" }}>
                        {tier === "free" ? "Free tier" : `${tier.charAt(0).toUpperCase() + tier.slice(1)} plan`}
                        {isTrial && (
                            <span
                                className="ml-2 atlas-pill text-[10px]"
                                style={{
                                    background: "var(--atlas-success-muted)",
                                    color: "#34d399",
                                    border: "1px solid rgba(16,185,129,0.45)",
                                }}
                                data-testid="usage-widget-trial-chip"
                            >
                                <Clock className="w-2.5 h-2.5" strokeWidth={2} />
                                {trial.trial_days_remaining} days left
                            </span>
                        )}
                    </h3>
                </div>
                <Link
                    to="/app/billing"
                    className="atlas-btn-ghost inline-flex items-center gap-1 rounded-md px-3 h-8 text-[12px] font-medium"
                    data-testid="usage-widget-upgrade-link"
                >
                    {tier === "free" ? "Upgrade" : "Manage plan"} <ArrowRight className="w-3 h-3" strokeWidth={2} />
                </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {PRIMARY_ORDER.map((key) => (
                    <UsageRow key={key} feature={key} data={usage[key]} />
                ))}
            </div>

            {SECONDARY_ORDER.some((k) => usage[k] && (usage[k].cap == null || usage[k].current > 0)) && (
                <button
                    onClick={() => setShowAll((v) => !v)}
                    className="mt-3 text-[11.5px] font-medium inline-flex items-center gap-1 hover:opacity-90"
                    style={{ color: "var(--atlas-primary-hover)" }}
                    data-testid="usage-widget-toggle-more"
                >
                    {showAll ? "Hide workspace AI usage" : "Show workspace AI usage"} <ArrowRight className={`w-3 h-3 transition-transform ${showAll ? "rotate-90" : ""}`} strokeWidth={2} />
                </button>
            )}

            {showAll && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {SECONDARY_ORDER.map((key) => (
                        <UsageRow key={key} feature={key} data={usage[key]} />
                    ))}
                </div>
            )}
        </section>
    );
}

function UsageRow({ feature, data }) {
    const meta = FEATURE_META[feature] || {};
    const Icon = meta.Icon || Sparkles;
    if (!data) return null;
    const cap = data.cap;
    const current = data.current || 0;
    const unlimited = cap == null;
    const pctRaw = unlimited ? 0 : (current / Math.max(1, cap));
    const pct = Math.min(100, Math.round(pctRaw * 100));
    const warn = pct >= 80;
    const empty = cap === 0;
    return (
        <div
            data-testid={`usage-row-${feature}`}
            className="atlas-card p-3 flex items-center gap-3"
            style={{ background: "var(--atlas-bg-highlight)" }}
        >
            <div
                className="w-8 h-8 rounded-lg grid place-items-center flex-shrink-0"
                style={{
                    background: unlimited ? "var(--atlas-success-muted)" : "var(--atlas-primary-muted)",
                    border: unlimited ? "1px solid rgba(16,185,129,0.45)" : "1px solid rgba(139,92,246,0.35)",
                }}
            >
                <Icon className="w-3.5 h-3.5" strokeWidth={1.75} style={{ color: unlimited ? "#34d399" : "var(--atlas-primary-hover)" }} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                    <div className="text-[12px] font-medium truncate" style={{ color: "var(--atlas-text-primary)" }}>
                        {meta.label}
                    </div>
                    <div className="text-[11px] tabular-nums flex-shrink-0" style={{ color: warn ? "#fbbf24" : "var(--atlas-text-muted)" }}>
                        {unlimited ? (
                            <span style={{ color: "#34d399" }}>Unlimited</span>
                        ) : empty ? (
                            <span style={{ color: "var(--atlas-text-muted)" }}>Paid plan</span>
                        ) : (
                            <>
                                <span style={{ color: "var(--atlas-text-secondary)" }}>{current.toLocaleString()}</span>
                                {" / "}
                                {cap.toLocaleString()}
                            </>
                        )}
                    </div>
                </div>
                {!unlimited && !empty && (
                    <div className="mt-1.5 h-1 rounded-full overflow-hidden" style={{ background: "var(--atlas-bg-elevated)" }}>
                        <div
                            className="h-full"
                            style={{
                                width: `${pct}%`,
                                background: warn
                                    ? "linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)"
                                    : "var(--atlas-gradient-btn)",
                            }}
                        />
                    </div>
                )}
                {empty && (
                    <div className="mt-1 text-[10.5px]" style={{ color: "var(--atlas-text-muted)" }}>
                        Available on paid plans
                    </div>
                )}
            </div>
        </div>
    );
}
